import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';

import { useButton, useDeviceInfo, useI18n, useSelector } from '~Hooks';
import { useCart } from '~Components/CartProvider';
import { useStorage } from '~Components/Storage';
import { BlackButton, GreenButton, RemoveButton } from '~Scenes/Handy/styles';
import ItemMessageButton from '../ItemMessageButton';
import ChangeCourseButton from '../ChangeCourseButton';
import Icon from '~ComponentsImproved/Icon';
import trashIcon from '~Assets/icons/delete.svg';
import copyIcon from '~Assets/icons/content.svg';
import msgIcon from '~Assets/icons/edit.svg';
import courseIcon from '~Assets/icons/menu_book.svg';
import { usePage } from '~Scenes/Handy/Context/PageProvider';
import { CompleteBongQuery_venue_company_categories_items as BongQueryItem } from '~Scenes/POS/Queries/__queries__';

const ButtonsWrapper = styled.div`
  position: absolute;
  z-index: 2;
  display: flex;
  flex-direction: row;
  bottom: 0;
  left: 0;
  width: 80px;
  overflow: hidden;
`;

const Buttons = styled.div`
  transition: transform 0.2s cubic-bezier(0.215, 0.61, 0.355, 1);
  display: flex;
  // Align items to the bottom
  align-items: flex-end;
`;

const ButtonColumn = styled.div<{ isPhone?: boolean }>`
  width: 80px;
  padding: 0 8px;
  box-sizing: border-box;
  & > * {
    margin-bottom: 8px;
  }
  padding-bottom: ${({ isPhone }) => (isPhone ? '16px' : 0)};
`;

const ExtraButtonsColumn = styled(ButtonColumn)`
  & > div {
    text-transform: capitalize;
    font-size: 8px;
    display: flex;
    flex-flow: column nowrap;
    padding: 2px 4px;
    justify-content: center;
    align-items: center;
    height: 48px;
  }
`;

const GET_COURSE_TYPES = gql`
  query getCompanyData($companyId: ID!) {
    Company(id: $companyId) {
      id
      courseTypes {
        id
      }
    }
  }
`;

interface Props {
  onClear: () => void;
  onSend: () => void;
  onCancel: () => void;
  cartOpen: boolean;
  handleItemPress: (
    item: BongQueryItem,
    manualPrice?: number,
    volume?: number,
    message?: string,
  ) => void;
  setSubPage: (subPage: any) => void;
}

/**
 * These actions are rendered as a stack of buttons aboslute positioned to the left
 * They include actions related to the cart, and items selected in cart
 */
const CartActions: React.FC<Props> = ({
  onClear,
  onSend,
  onCancel,
  cartOpen,
  handleItemPress,
  setSubPage,
}) => {
  const companyId = useSelector((state) => state.company);
  const { data } = useQuery(GET_COURSE_TYPES, {
    variables: {
      companyId,
    },
  });
  const deviceInfo = useDeviceInfo();
  const cart = useCart();
  const { i18n } = useI18n();
  const [storage] = useStorage();
  const { current } = usePage();

  const deviceWakeState = useSelector((state) => state.deviceWakeState);

  const isQSR = current === 'POS';

  const sendClickHandler = useButton({
    onClick: onSend,
    disabled: !isQSR && deviceWakeState.syncingTableEvents,
    disabledToast: !isQSR ? i18n('Handy.Tables.LoadingEvents') : undefined,
  });

  const cancelClickHandlers = useButton({
    onClick: onCancel,
  });

  const handleRemoveItem = () => {
    cart.selectedItems.remove();
  };

  const clearButton = useButton({
    onClick: onClear,
  });

  const copyButton = useButton({
    onClick: () => {
      cart.selectedItems.duplicate();
    },
  });

  const itemsAreSelected = cart.selectedItems.ids.length > 0;

  return (
    <ButtonsWrapper>
      <Buttons
        style={{
          transform: itemsAreSelected ? `translateX(-80px)` : 'translateX(0px)',
        }}>
        <ButtonColumn isPhone={deviceInfo?.isPhone}>
          {cart.items.length > 0 ? (
            <>
              {/* Only show the clear button when the cart is open in the QSR, to save left column space */}
              {cartOpen && (
                <BlackButton data-testid="undo" {...clearButton}>
                  {i18n('General.Clear')}
                </BlackButton>
              )}
              <GreenButton
                {...sendClickHandler}
                data-testid="send-order"
                fontSize={isQSR ? '10px' : '12px'}>
                {isQSR
                  ? storage.handyDirectPayment
                    ? i18n('Handy.Ordering.Pay')
                    : i18n('Handy.Ordering.Continue')
                  : i18n('Handy.Ordering.Send')}
              </GreenButton>
            </>
          ) : (
            !isQSR && (
              <BlackButton fontSize="10px" {...cancelClickHandlers}>
                {i18n('Handy.Ordering.Cancel')}
              </BlackButton>
            )
          )}
        </ButtonColumn>
        {itemsAreSelected && (
          <ExtraButtonsColumn isPhone={deviceInfo?.isPhone}>
            <RemoveButton onClick={handleRemoveItem}>
              <Icon src={trashIcon} width="20px" height="20px" />
              {i18n('Order.Remove')}
            </RemoveButton>
            {data?.Company?.courseTypes?.length > 0 && (
              <ChangeCourseButton>
                <Icon src={courseIcon} width="20px" height="20px" />
                {i18n('Handy.Ordering.ChangeCourse')}
              </ChangeCourseButton>
            )}
            <ItemMessageButton>
              <Icon src={msgIcon} width="20px" height="20px" />
              {i18n('Handy.Ordering.Message')}
            </ItemMessageButton>
            <BlackButton {...copyButton}>
              <Icon src={copyIcon} width="20px" height="20px" />
              <span>{i18n('Handy.Ordering.Copy')}</span>
            </BlackButton>
          </ExtraButtonsColumn>
        )}
      </Buttons>
    </ButtonsWrapper>
  );
};

export default CartActions;
